/**
 * Reply-targeting §3 — pure cursor → spine resolver. The cursor resolves to
 * its SPINE, never a mid-spine position (spec §1). Pure module (no React, no
 * api imports) so CRA-jest never touches the import.meta chain.
 */
import { trailResidents } from './trailResidents';

export interface CursorTarget {
  cursorTurnId: string;
  /** null when the cursor sits on the main spine. */
  spineRootMessageId: string | null;
  isMainSpine: boolean;
  trailId: string | null;
  /** Display name for pill copy — uuids are banned in operator copy. */
  residentName: string | null;
  /** Shared spines P2 — ALL residents on the cursor's trail (the walk-up
   *  finds one root; the pill must name everyone who will answer).
   *  OPTIONAL: pre-P2 cursor literals construct without it. */
  residentNames?: string[];
}

interface MinimalMsg {
  id: string;
  parent_ids?: string[];
  metadata?: Record<string, any>;
}

const MAX_WALK = 500;

export function resolveCursorTarget(
  cursorTurnId: string | null,
  messages: MinimalMsg[],
): CursorTarget | null {
  if (!cursorTurnId) return null;
  const byId = new Map(messages.map((m) => [m.id, m]));
  let cur = byId.get(cursorTurnId);
  if (!cur) return null;  // stale cursor (message gone) self-heals to no-target

  const seen = new Set<string>();
  for (let i = 0; cur && i < MAX_WALK; i++) {
    if (seen.has(cur.id)) break;        // cycle guard — treat as main spine
    seen.add(cur.id);
    if (cur.metadata?.is_spine_root === true) {
      const trailId = cur.metadata?.trail_id ?? null;
      return {
        cursorTurnId,
        spineRootMessageId: cur.id,
        isMainSpine: false,
        trailId,
        residentName: cur.metadata?.persona_display_name_at_join ?? null,
        residentNames: trailResidents(messages, trailId)
          .map((r) => r.name).filter(Boolean) as string[],
      };
    }
    const parentId = cur.parent_ids?.[0];
    cur = parentId ? byId.get(parentId) : undefined;
  }
  const original = byId.get(cursorTurnId)!;
  return {
    cursorTurnId,
    spineRootMessageId: null,
    isMainSpine: true,
    trailId: original.metadata?.trail_id ?? null,
    residentName: null,
  };
}
